import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { Spinner, Alert, Modal } from '../../components/UI';
import logo from '../../assets/logo.jpg';

function formatTime(sec) {
  if (sec == null) return '--:--';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function ExamPage() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [questions, setQuestions] = useState(null);
  const [config, setConfig] = useState({ allow_copy_paste: false, tab_switch_detection: true });
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({}); // qid -> {selected_option_id, answer_text}
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [error, setError] = useState('');
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [endedByAdmin, setEndedByAdmin] = useState(false);
  const submittedRef = useRef(false);
  const loggedOutRef = useRef(false);
  const saveTimers = useRef({});
  const pendingSaves = useRef({}); // qid -> payload not yet written to the server
  const navRef = useRef(null);
  const currentBtnRef = useRef(null);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    try {
      const [active, qData] = await Promise.all([
        api.get('/exam/active'),
        api.get(`/exam/attempts/${attemptId}/questions`),
      ]);
      setConfig(active.config || {});
      setQuestions(qData.questions);
      setSecondsLeft(qData.seconds_left);
      const initAnswers = {};
      qData.questions.forEach(q => {
        initAnswers[q.id] = { selected_option_id: q.saved_option_id, answer_text: q.saved_answer_text || '' };
      });
      setAnswers(initAnswers);
    } catch (e) {
      setError(e.message);
    }
  }

  const handleSubmit = useCallback(async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      await api.post(`/exam/attempts/${attemptId}/submit`, {});
      navigate('/my-results', { replace: true });
    } catch (e) {
      setError(e.message);
      submittedRef.current = false;
      setSubmitting(false);
    }
  }, [attemptId, navigate]);

  // Write one debounced answer through to the server.
  const flushAnswer = useCallback(async (qid) => {
    const payload = pendingSaves.current[qid];
    if (!payload) return;
    delete pendingSaves.current[qid];
    try {
      await api.post(`/exam/attempts/${attemptId}/answer`, { question_id: qid, ...payload });
    } catch (e) { /* silent autosave failure */ }
  }, [attemptId]);

  // Write every still-debounced answer now. Used before we sign the candidate
  // out on a tab switch, so their last selection isn't lost with the session.
  const flushPendingAnswers = useCallback(async () => {
    Object.values(saveTimers.current).forEach(clearTimeout);
    saveTimers.current = {};
    await Promise.all(Object.keys(pendingSaves.current).map(flushAnswer));
  }, [flushAnswer]);

  // Countdown timer
  useEffect(() => {
    if (secondsLeft == null) return;
    if (secondsLeft <= 0) { handleSubmit(); return; }
    const t = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) { clearInterval(t); handleSubmit(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [secondsLeft === null, handleSubmit]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect an admin stopping the exam. The server force-submits/evaluates the
  // attempt on stop and /exam/active stops returning the live paper, so poll it
  // and end the exam locally when that happens (no submit call - it's already
  // been submitted server-side; that would 409).
  useEffect(() => {
    if (!questions) return;
    let cancelled = false;
    const check = async () => {
      if (submittedRef.current || loggedOutRef.current || cancelled) return;
      try {
        const active = await api.get('/exam/active');
        if (active.config) setConfig(active.config); // pick up live setting changes (e.g. copy-paste) mid-exam
        const stopped = !active.paper || (active.attempt && active.attempt.status !== 'in_progress');
        if (stopped && !submittedRef.current && !cancelled) {
          submittedRef.current = true; // silence timer auto-submit + beforeunload warning
          setEndedByAdmin(true);
        }
      } catch (e) { /* transient network error - keep polling */ }
    };
    const t = setInterval(check, 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, [questions]);

  // Once ended by the admin, send the user to their results after a brief notice.
  useEffect(() => {
    if (!endedByAdmin) return;
    const t = setTimeout(() => navigate('/my-results', { replace: true }), 5000);
    return () => clearTimeout(t);
  }, [endedByAdmin, navigate]);

  // Anti-cheat: block copy/paste/context-menu when disabled
  useEffect(() => {
    if (config.allow_copy_paste) return;
    const block = (e) => e.preventDefault();
    document.addEventListener('copy', block);
    document.addEventListener('paste', block);
    document.addEventListener('cut', block);
    document.addEventListener('contextmenu', block);
    return () => {
      document.removeEventListener('copy', block);
      document.removeEventListener('paste', block);
      document.removeEventListener('cut', block);
      document.removeEventListener('contextmenu', block);
    };
  }, [config.allow_copy_paste]);

  // Anti-cheat: tab-switch / visibility detection.
  // Leaving the exam tab counts the switch and signs the candidate out. The
  // attempt is NOT submitted and the clock is not paused - it runs off
  // started_at on the server - so every minute they stay signed out is exam
  // time they lose. They can log back in and resume with whatever is left.
  useEffect(() => {
    if (!config.tab_switch_detection) return;
    const onVisibility = async () => {
      if (!document.hidden || submittedRef.current || loggedOutRef.current) return;
      loggedOutRef.current = true;
      // Answers first, while the token is still valid.
      await flushPendingAnswers();
      try {
        await api.post(`/exam/attempts/${attemptId}/tab-switch`, { forced_logout: true });
      } catch (e) { /* sign out anyway - the switch already happened */ }
      logout();
      navigate('/login', {
        replace: true,
        state: {
          notice: 'You were signed out because you left the exam tab. Your answers are saved and the exam is not submitted, but the timer kept running - sign in again and resume immediately.',
        },
      });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [config.tab_switch_detection, attemptId, flushPendingAnswers, logout, navigate]);

  // Keep the active question visible when the navigator's own scroller is in
  // play (long papers). Scrolls the navigator only - never the page.
  useEffect(() => {
    const nav = navRef.current;
    const btn = currentBtnRef.current;
    if (!nav || !btn || nav.scrollHeight <= nav.clientHeight) return;
    const b = btn.getBoundingClientRect();
    const n = nav.getBoundingClientRect();
    if (b.top < n.top) nav.scrollTop -= (n.top - b.top) + 6;
    else if (b.bottom > n.bottom) nav.scrollTop += (b.bottom - n.bottom) + 6;
  }, [current]);

  // Warn before closing tab mid-exam
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!submittedRef.current && !loggedOutRef.current) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  function saveAnswer(qid, payload) {
    if (loggedOutRef.current) return;
    setAnswers(prev => ({ ...prev, [qid]: { ...prev[qid], ...payload } }));
    pendingSaves.current[qid] = { ...(pendingSaves.current[qid] || {}), ...payload };
    clearTimeout(saveTimers.current[qid]);
    saveTimers.current[qid] = setTimeout(() => { flushAnswer(qid); }, 500);
  }

  if (error && !questions) return <div className="content-narrow mt-4"><Alert type="danger">{error}</Alert></div>;
  if (!questions) return <Spinner />;

  const q = questions[current];
  const ans = answers[q.id] || {};
  const answeredCount = questions.filter(qq => {
    const a = answers[qq.id];
    return a && (a.selected_option_id || (a.answer_text && a.answer_text.trim()));
  }).length;

  const low = secondsLeft != null && secondsLeft <= 120;

  return (
    <div className="exam-shell">
      <div className="exam-topbar">
        <div className="exam-brand">
          <img src={logo} alt="Yashoda Hospitals" />
          <div>
            <div className="brand-name">Yashoda Hospitals</div>
            <span className="text-muted text-sm">
              Question {current + 1} of {questions.length} &middot; {answeredCount} answered
            </span>
          </div>
        </div>
        <div className="flex-gap">
          <div className={`exam-timer ${low ? 'low' : ''}`}>{formatTime(secondsLeft)}</div>
          <button className="btn btn-accent" onClick={() => setConfirmSubmit(true)} disabled={submitting}>
            Submit exam
          </button>
        </div>
      </div>

      <Alert type="danger">{error}</Alert>

      <div className="exam-body">
        <div className="card">
          <div className="text-muted text-sm mb-0">Marks: {q.max_score}</div>
          <h3 style={{ marginTop: 8 }}>{q.question_text}</h3>

          {q.question_type === 'mcq' ? (
            <div className="mt-4">
              {q.options.map((opt, i) => (
                <div
                  key={opt.id}
                  className={`option-item ${ans.selected_option_id === opt.id ? 'selected' : ''}`}
                  onClick={() => saveAnswer(q.id, { selected_option_id: opt.id, answer_text: null })}
                >
                  <span className="option-label">{String.fromCharCode(65 + i)}</span>
                  <span>{opt.option_text}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-4 form-group">
              <label>{q.question_type === 'fill_blank' ? 'Your answer' : 'Your answer (short answer)'}</label>
              <textarea
                value={ans.answer_text || ''}
                onChange={(e) => saveAnswer(q.id, { answer_text: e.target.value, selected_option_id: null })}
                placeholder="Type your answer here"
                rows={q.question_type === 'short_answer' ? 4 : 2}
              />
            </div>
          )}

          <div className="flex-between mt-4">
            <button className="btn btn-outline" disabled={current === 0} onClick={() => setCurrent(c => c - 1)}>
              Previous
            </button>
            <button
              className="btn btn-primary"
              disabled={current === questions.length - 1}
              onClick={() => setCurrent(c => c + 1)}
            >
              Next
            </button>
          </div>
        </div>

        <div className="card q-nav-card">
          <div className="q-nav-head">
            <h3 className="q-nav-title">Question navigator</h3>
            <span className="q-nav-count">{answeredCount}/{questions.length}</span>
          </div>

          <div className="question-nav" ref={navRef}>
            {questions.map((qq, i) => {
              const a = answers[qq.id];
              const answered = a && (a.selected_option_id || (a.answer_text && a.answer_text.trim()));
              const isCurrent = i === current;
              return (
                <button
                  key={qq.id}
                  type="button"
                  ref={isCurrent ? currentBtnRef : null}
                  className={`q-nav-btn ${answered ? 'answered' : ''} ${isCurrent ? 'current' : ''}`}
                  onClick={() => setCurrent(i)}
                  aria-current={isCurrent ? 'true' : undefined}
                  aria-label={`Question ${i + 1}${answered ? ', answered' : ', not answered'}`}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>

          <div className="q-nav-legend">
            <span><i className="q-nav-swatch current" />Current</span>
            <span><i className="q-nav-swatch answered" />Answered</span>
            <span><i className="q-nav-swatch" />Not answered</span>
          </div>
        </div>
      </div>

      {endedByAdmin && (
        <Modal title="Exam ended">
          <p>
            The administrator has ended this exam. Your answers have been submitted automatically.
            You'll be taken to your results now.
          </p>
          <div className="modal-actions">
            <button className="btn btn-accent" onClick={() => navigate('/my-results', { replace: true })}>
              View results
            </button>
          </div>
        </Modal>
      )}

      {confirmSubmit && (
        <Modal title="Submit exam?" onClose={() => setConfirmSubmit(false)}>
          <p>
            You've answered {answeredCount} of {questions.length} questions. Once submitted, you cannot change your
            answers. Are you sure you want to submit?
          </p>
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setConfirmSubmit(false)}>Keep reviewing</button>
            <button className="btn btn-accent" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Submitting…' : 'Yes, submit'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
